import { Colors, tokens } from '@gripp/shared-ui';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { ActivityIndicator, Modal, Portal, Text } from 'react-native-paper';
import QRCode from 'react-native-qrcode-svg';
import { CopyText } from '../../shared/components/copyText';
import { useCreateIssueShareLink } from '../hooks/useShareIssue';

type ShareIssuePanelProps = {
  issueId: string;
  visible: boolean;
  onDismiss: () => void;
};

export const ShareIssuePanel = ({
  issueId,
  visible,
  onDismiss,
}: ShareIssuePanelProps) => {
  const { t } = useTranslation();
  const { mutateAsync, isPending } = useCreateIssueShareLink();
  const [url, setUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!visible) {
      setUrl(null);
      setExpiresAt(null);
      setError(false);
      return;
    }
    let cancelled = false;
    const mint = async () => {
      try {
        const result: any = await mutateAsync({
          subjectType: 'ISSUE',
          subjectId: issueId,
        });
        if (cancelled) return;
        const link = result?.share?.createShareLink;
        if (!link?.token) {
          setError(true);
          return;
        }
        setUrl(`${window.location.origin}/share/${link.token}`);
        setExpiresAt(link.expiresAt ?? null);
      } catch {
        if (!cancelled) setError(true);
      }
    };
    mint();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, issueId]);

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={styles.modal}
      >
        <Text style={styles.title}>
          {t('share.title', 'Share this activity')}
        </Text>
        <Text style={styles.subtitle}>
          {t(
            'share.subtitle',
            'Anyone with this link can sign in to view and reply.'
          )}
        </Text>

        {isPending && !url && (
          <ActivityIndicator style={styles.loader} color={Colors.primaryDark} />
        )}

        {error && (
          <Text style={styles.error}>
            {t(
              'share.error',
              'Could not create a share link. Please try again.'
            )}
          </Text>
        )}

        {url && (
          <View style={styles.body}>
            <View style={styles.qr}>
              <QRCode value={url} size={180} />
            </View>
            <Text style={styles.url} numberOfLines={2}>
              {url}
            </Text>
            <CopyText
              textToCopy={url}
              buttonText={t('share.copyLink', 'Copy link')}
              successMessage={t('share.copied', 'Link copied')}
              errorMessage={t('share.copyError', 'Could not copy link')}
            />
            {expiresAt && (
              // Stated up front so a member resharing an aged-out link is not
              // left guessing why the recipient sees an error.
              <Text style={styles.expiry}>
                {t('share.expiresOn', {
                  date: moment(expiresAt).format('LL'),
                })}
              </Text>
            )}
          </View>
        )}

        <Pressable
          style={styles.done}
          onPress={onDismiss}
          accessibilityRole="button"
          accessibilityLabel={t('common.done', 'Done')}
        >
          <Text style={styles.doneText}>{t('common.done', 'Done')}</Text>
        </Pressable>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: Colors.role.surface,
    marginHorizontal: 'auto',
    borderRadius: tokens.borderRadius.md,
    padding: tokens.spacing.xl,
    width: 360,
    maxWidth: '90%',
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  title: {
    ...tokens.typography.title.small,
    color: Colors.role.textStrong,
    textAlign: 'center',
  },
  subtitle: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSecondary,
    textAlign: 'center',
  },
  loader: {
    marginVertical: tokens.spacing.lg,
  },
  error: {
    ...tokens.typography.label.medium,
    color: Colors.semantic.error,
    textAlign: 'center',
  },
  body: {
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  qr: {
    padding: tokens.spacing.md,
    backgroundColor: Colors.white,
  },
  url: {
    ...tokens.typography.label.small,
    color: Colors.role.textSecondary,
    textAlign: 'center',
  },
  expiry: {
    ...tokens.typography.label.small,
    color: Colors.role.textSecondary,
    textAlign: 'center',
  },
  done: {
    marginTop: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.xl,
  },
  doneText: {
    ...tokens.typography.label.largeEmphasised,
    color: Colors.primaryDark,
  },
});
